import { useState, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { useRelayEnvironment, fetchQuery } from 'react-relay';
// @ts-expect-error - TS2305 - Module '"relay-runtime"' has no exported member 'Query'.
import type { FetchQueryFetchPolicy, Query, Variables } from 'relay-runtime';

// sounced from https://github.com/apollographql/apollo-client/blob/main/src/react/hooks/utils/useDeepMemo.ts
const useDeepMemo = <TKey, TValue>(memoFn: () => TValue, key: TKey): TValue => {
	const ref = useRef<unknown>();

	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	if (!ref.current || !isEqual(key, ref.current.key)) {
		ref.current = { key, value: memoFn() };
	}

	// @ts-expect-error - TS2571 - Object is of type 'unknown'.
	return ref.current.value;
};

type UseFetchQueryFetchOptions = Readonly<{
	fetchPolicy?: FetchQueryFetchPolicy;
	skip?: boolean;
}>;

// This hook is used to ease the migration from Apollo to Relay.
// You shouldn't use it unless you are migrating apollo-client to Relay.
// Also, be mindful that this hook won't trigger re-render if the Relay Store changes.
export const useFetchQuery = <TVariables extends Variables, TData, TRawResponse = undefined>(
	query: Query<TVariables, TData, TRawResponse>,
	vars: TVariables,
	opts?: UseFetchQueryFetchOptions,
): {
	data: TData | null;
	error: Error | null;
	loading: boolean;
} => {
	const environment = useRelayEnvironment();
	const [result, setResult] = useState({
		data: null,
		error: null,
		loading: true,
	});
	const { data, error, loading } = result;
	const prevVariables = useRef<unknown>();

	const fetch = (
		variables: TVariables | Variables,
		options: undefined | UseFetchQueryFetchOptions,
	) => {
		const { fetchPolicy = 'network-only', skip = false } = options || {};
		if (skip || isEqual(prevVariables.current, variables)) return;
		prevVariables.current = variables;

		// Don't trigger re-render if it's intial render
		if (!(loading === true && data == null && error === null)) {
			setResult({
				error: null,
				loading: true,
				data: null,
			});
		}
		fetchQuery(environment, query, variables, { fetchPolicy })
			.toPromise()
			.then((d) => {
				setResult({
					error: null,
					loading: false,
					// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'null'.
					data: d,
				});
			})
			.catch((e) => {
				setResult({
					data: null,
					loading: false,
					error: e,
				});
			});
	};

	const memo = {
		vars,
		opts,
	};

	useDeepMemo(() => {
		fetch(vars, opts);
	}, memo);
	const nullableData = data == null ? null : data;

	return { data: nullableData, error, loading };
};
